import * as Sentry from '@sentry/react';

import Button from '~/components/common/Button';
import BoundaryError from '~/errors/BoundaryError';
import GA from '~/services/GA/GA';
import Context from '~/stores/context';
import environment from '~/stores/providers/environment';
import logging from '~/stores/providers/logging';
import PropTypes from 'prop-types';
import * as React from 'react';
import { Component } from 'react';
import styled from 'styled-components';

const ErrorWrapper = styled.div`
  font-size: 24px;
  text-align: center;
`;

const CenterContent = styled.div`
  display: flex;
  width: 100vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
`;

const RefreshButton = styled(Button)`
  display: inline-block;
  width: 50%;
  height: 48px;
`;

class ErrorBoundary extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  canReloadError(errorString) {
    return (
      errorString.includes('Minified React Error') ||
      errorString.includes('ChunkLoadError') ||
      errorString.includes('Loading CSS Chunk')
    );
  }

  sendSentryError(errorString) {
    const chunkLoadErrorPattern = /Loading (CSS )?chunk/;
    return !chunkLoadErrorPattern.test(errorString);
  }

  componentDidCatch(error, info) {
    const errorString = error.stack || '';
    logging.log('Rendering Error', error, info);
    this.setState({ hasError: true });

    GA.writeRenderingError(error, info);

    if (this.canReloadError(errorString)) {
      Context.uiState.reload();
    }

    if (environment.sentryDsn && this.sendSentryError(error.message)) {
      Sentry.captureException(new BoundaryError(error.message), { level: 'fatal', extra: info });
    }
  }

  reload = () => {
    window.location.reload();
  };

  render() {
    return this.state.hasError ? (
      <CenterContent>
        <ErrorWrapper>
          <p>An error has occurred</p>
          <RefreshButton onClick={this.reload} styles="action">
            Refresh
          </RefreshButton>
        </ErrorWrapper>
      </CenterContent>
    ) : (
      this.props.children
    );
  }
}

export default ErrorBoundary;
